<template>
  <main class="rm-page finish-form-page">
    <header class="rm-topbar rm-topbar--back-only">
      <button
        class="rm-icon-button"
        type="button"
        aria-label="Volver"
        @click="goBack"
      >
        <span aria-hidden="true"></span>
      </button>

      <div class="rm-heading">
        <h1 class="rm-title">Finalizar {{ stopTypeLabel }}</h1>
      </div>
    </header>

    <section class="finish-form-state-shell" aria-live="polite">
      <div v-if="isLoading" class="finish-form-state">
        <span class="finish-form-spinner" aria-hidden="true"></span>
        <p>Cargando parada...</p>
      </div>

      <div v-else-if="errorMessage" class="finish-form-state">
        <h2>No se pudo preparar la finalización</h2>
        <p>{{ errorMessage }}</p>
        <button
          class="rm-button rm-button--outline"
          type="button"
          :disabled="isLoading"
          @click="loadState"
        >
          Reintentar
        </button>
      </div>

      <section
        v-else
        class="finish-form-content"
        aria-label="Formulario de finalización"
        :aria-busy="isSubmitting"
      >
        <div class="finish-form-scroll">
          <p v-if="chipsText" class="rm-chips">{{ chipsText }}</p>

          <section
            class="finish-form-stop-options"
            aria-label="Resultado de parada"
          >
            <button
              class="finish-form-option"
              :class="{
                'finish-form-option--selected':
                  selectedStopResult === stopResultValues.correct,
              }"
              type="button"
              @click="selectCorrect"
            >
              <span class="finish-form-option__icon-shell" aria-hidden="true">
                <span
                  class="finish-form-option__icon finish-form-option__icon--success"
                ></span>
              </span>
              <span class="finish-form-option__label">Correcto</span>
              <span
                class="finish-form-option__marker"
                :class="{
                  'finish-form-option__marker--active':
                    selectedStopResult === stopResultValues.correct,
                }"
                aria-hidden="true"
              ></span>
            </button>

            <button
              class="finish-form-option"
              :class="{
                'finish-form-option--selected':
                  selectedStopResult === stopResultValues.incident,
              }"
              type="button"
              @click="selectIncident"
            >
              <span class="finish-form-option__icon-shell" aria-hidden="true">
                <span
                  class="finish-form-option__icon finish-form-option__icon--warning"
                ></span>
              </span>
              <span class="finish-form-option__label">Incidencia</span>
              <span
                class="finish-form-option__marker"
                :class="{
                  'finish-form-option__marker--active':
                    selectedStopResult === stopResultValues.incident,
                }"
                aria-hidden="true"
              ></span>
            </button>
          </section>

          <section class="finish-form-observations">
            <label for="finish-load-unload-observations">OBSERVACIONES</label>
            <textarea
              id="finish-load-unload-observations"
              v-model="observations"
              maxlength="200"
              rows="5"
            ></textarea>
          </section>

          <p
            v-if="submitMessage"
            class="finish-form-message"
            :class="`finish-form-message--${submitMessageType}`"
          >
            {{ submitMessage }}
          </p>
        </div>

        <footer class="finish-form-footer">
          <button
            class="rm-button finish-form-footer__submit"
            type="button"
            :disabled="!isFinalizeEnabled"
            @click="submitFinalize"
          >
            <img
              class="rm-button__icon-img"
              :src="sendIcon"
              alt=""
              aria-hidden="true"
            />
            <span>Finalizar {{ stopTypeLabel }}</span>
          </button>
        </footer>

        <div
          v-if="isSubmitting"
          class="finish-form-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <span class="finish-form-spinner" aria-hidden="true"></span>
          <p>Finalizando parada...</p>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { mobileRouteStore } from "@/_store/mobileRouteStore";
import sendIcon from "../../../../assets/img/mobile-images/send.svg";
import { loadRouteStopDetail } from "../shared/loadRouteStopDetail";
import { buildPackageChips, resolveStopTypeLabel } from "../day-route/dayRouteModel";
import {
  canFinalizeLoadUnload,
  createLoadUnloadFinalizePayload,
  stopResultValues,
  validateLoadUnloadFinalize,
} from "./finishLoadUnloadModel";

const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
  context: {
    type: Object,
    default() {
      return null;
    },
  },
});
const emit = defineEmits(["back", "completed"]);
const mobileRoutesStore = mobileRouteStore();

const planId = ref("");
const routeStopId = ref("");
const routeStop = ref(null);
const selectedStopResult = ref(stopResultValues.correct);
const observations = ref("");
const isLoading = ref(false);
const isSubmitting = ref(false);
const errorMessage = ref("");
const submitMessage = ref("");
const submitMessageType = ref("info");
let abortController = null;

const stopTypeLabel = computed(() =>
  resolveStopTypeLabel(routeStop.value?.stop.manualType, routeStop.value?.stop.type)
);
const chipsText = computed(() =>
  buildPackageChips(routeStop.value).join(" · ")
);
const isFinalizeEnabled = computed(() =>
  canFinalizeLoadUnload({
    isSubmitting: isSubmitting.value,
    routeStop: routeStop.value,
  })
);

onMounted(() => {
  readRouteQuery();
  loadState();
});

onBeforeUnmount(() => {
  abortCurrentRequest();
});

async function loadState() {
  if (isLoading.value) {
    return;
  }

  if (!routeStopId.value) {
    errorMessage.value = "No se recibió la parada en la URL.";
    return;
  }

  abortCurrentRequest();
  abortController = new AbortController();

  isLoading.value = true;
  errorMessage.value = "";
  submitMessage.value = "";

  try {
    routeStop.value = props.context?.routeStop ?? null;

    if (!routeStop.value && planId.value) {
      routeStop.value = await loadRouteStopDetail({
        routeStop: props.context?.routeStop ?? null,
        planId: planId.value,
        routeStopId: routeStopId.value,
        vehicleId: (props.context?.vehicleId || "").toString(),
        signal: abortController.signal,
      });
    }

    if (!routeStop.value) {
      errorMessage.value = "No se encontró la parada seleccionada.";
    }
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    errorMessage.value = error.message || "No se pudo cargar la parada.";
  } finally {
    isLoading.value = false;
    abortController = null;
  }
}

function selectCorrect() {
  selectedStopResult.value = stopResultValues.correct;
}

function selectIncident() {
  selectedStopResult.value = stopResultValues.incident;
}

async function submitFinalize() {
  if (!isFinalizeEnabled.value) {
    return;
  }

  const validationError = validateLoadUnloadFinalize({
    selectedStopResult: selectedStopResult.value,
    observations: observations.value,
  });

  if (validationError) {
    submitMessage.value = validationError;
    submitMessageType.value = "warning";
    return;
  }

  const effectivePlanId = routeStop.value?.stop.planId || planId.value;
  const effectiveRouteStopId = routeStop.value?.stop.id || routeStopId.value;

  if (!effectivePlanId || !effectiveRouteStopId) {
    submitMessage.value = "No se pudo resolver el plan o la parada.";
    submitMessageType.value = "warning";
    return;
  }

  abortCurrentRequest();
  abortController = new AbortController();

  isSubmitting.value = true;
  submitMessage.value = "";

  try {
    const payload = createLoadUnloadFinalizePayload({
      selectedStopResult: selectedStopResult.value,
      observations: observations.value,
    });

    await mobileRoutesStore.finalizeRouteStop({
      planId: effectivePlanId,
      routeStopId: effectiveRouteStopId,
      mode: "LoadUnload",
      payload,
      signal: abortController.signal,
    });

    submitMessage.value = "Parada finalizada correctamente.";
    submitMessageType.value = "success";

    if (props.embedded) {
      emit("completed", {
        planId: effectivePlanId,
        routeStopId: effectiveRouteStopId,
      });
      return;
    }

    const target = new URL("/", window.location.origin);
    target.searchParams.set("planId", effectivePlanId);
    window.location.assign(target.toString());
  } catch (error) {
    if (error.name === "AbortError") {
      return;
    }

    submitMessage.value = error.message || "No se pudo finalizar la parada.";
    submitMessageType.value = "warning";
  } finally {
    isSubmitting.value = false;
    abortController = null;
  }
}

function goBack() {
  if (props.embedded) {
    emit("back");
    return;
  }

  if (window.history.length > 1) {
    window.history.back();
    return;
  }

  const target = new URL("/service-detail", window.location.origin);

  if (planId.value) {
    target.searchParams.set("planId", planId.value);
  }

  target.searchParams.set("stopId", routeStopId.value);
  window.location.assign(target.toString());
}

function readRouteQuery() {
  if (props.embedded) {
    planId.value = props.context?.planId?.trim?.() ?? "";
    routeStopId.value =
      (props.context?.routeStopId || props.context?.stopId || "").trim?.() ??
      "";
    return;
  }

  const query = new URLSearchParams(window.location.search);
  planId.value = query.get("planId")?.trim() ?? "";
  routeStopId.value = query.get("stopId")?.trim() ?? "";
}

function abortCurrentRequest() {
  if (abortController) {
    abortController.abort();
    abortController = null;
  }
}
</script>

<style scoped src="../finish-form/finishForm.css"></style>
<style scoped src="../shared/routesMobileCommon.css"></style>
